/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Sidebar / panel-layout pane. Lets the user choose which side the
// Conversations list and the Threads/Notes/Tasks utility panel dock to.
// Persists through the shared `useUiPreferencesStore`, which the chat
// route layout (`ChatRouteHost`) reads to order its columns.

import { View } from "react-native";
import {
	useUiPreferencesStore,
	type PanelSide,
} from "../../preferences/uiPreferencesStore";
import { Select } from "../controls";
import { SettingsDivider, SettingsRow } from "../SettingsRow";

const SIDE_OPTIONS: readonly { readonly value: PanelSide; readonly label: string }[] = [
	{ value: "left", label: "Left" },
	{ value: "right", label: "Right" },
];

export function SidebarPane(): JSX.Element {
	const conversationsSide = useUiPreferencesStore((s) => s.conversationsSide);
	const setConversationsSide = useUiPreferencesStore((s) => s.setConversationsSide);
	const threadsSide = useUiPreferencesStore((s) => s.threadsSide);
	const setThreadsSide = useUiPreferencesStore((s) => s.setThreadsSide);
	const notesSide = useUiPreferencesStore((s) => s.notesSide);
	const setNotesSide = useUiPreferencesStore((s) => s.setNotesSide);
	const tasksSide = useUiPreferencesStore((s) => s.tasksSide);
	const setTasksSide = useUiPreferencesStore((s) => s.setTasksSide);

	return (
		<View className="flex flex-col gap-4">
			<SettingsRow
				variant="hero"
				title="Sidebar & panels"
				description="Choose which side each panel docks to. The chat always stays in the centre."
				testID="settings-sidebar-intro"
			/>

			<SettingsRow
				title="Conversations"
				description="Side the chat list docks to. Defaults to the left."
				testID="settings-sidebar-conversations"
			>
				<Select value={conversationsSide} onChange={setConversationsSide} options={SIDE_OPTIONS} />
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Threads"
				description="Side the thread panel docks to."
				testID="settings-sidebar-threads"
			>
				<Select value={threadsSide} onChange={setThreadsSide} options={SIDE_OPTIONS} />
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Notes"
				description="Side the notes panel docks to."
				testID="settings-sidebar-notes"
			>
				<Select value={notesSide} onChange={setNotesSide} options={SIDE_OPTIONS} />
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Tasks"
				description="Side the tasks panel docks to."
				testID="settings-sidebar-tasks"
			>
				<Select value={tasksSide} onChange={setTasksSide} options={SIDE_OPTIONS} />
			</SettingsRow>
		</View>
	);
}
